import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faSpinner, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { supabase } from '../lib/supabase';
import { PaymentService } from '../services/paymentService';

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const confirmPayment = async () => {
      try {
        const paymentStatus = searchParams.get('payment_status');
        const pfPaymentId = searchParams.get('pf_payment_id');
        const mPaymentId = searchParams.get('m_payment_id');
        const registrationId = sessionStorage.getItem('pendingRegistrationId');

        // PayFast only redirects to return_url after a successful payment
        // (cancellations go to cancel_url) and includes NO payment parameters
        // in the redirect. Treat arrival here as success unless PayFast
        // explicitly reports a failed/cancelled status.
        const isFailed =
          paymentStatus === 'CANCELLED' ||
          paymentStatus === 'FAILED' ||
          paymentStatus === '0';

        if (isFailed) {
          setStatus('error');
          setMessage('Payment was not completed successfully.');
          return;
        }

        // One payment can cover several registrations (self + minors) — they
        // all share the same payment_reference, so update the whole batch
        const paymentReference = sessionStorage.getItem('pendingPaymentReference');

        if (paymentReference || registrationId) {
          const update = supabase
            .from('registrations')
            .update({
              status: 'confirmed',
              payment_status: 'completed',
              payment_date: new Date().toISOString(),
            });

          const { error: regError } = paymentReference
            ? await update.eq('payment_reference', paymentReference)
            : await update.eq('id', registrationId as string);

          if (regError) {
            console.error('Failed to update registration after payment:', regError);
            setStatus('error');
            setMessage('Your payment was received, but we could not update your registration status. Please contact the organizer with your payment details.');
            return;
          }

          sessionStorage.removeItem('pendingRegistrationId');
          sessionStorage.removeItem('pendingPaymentReference');
        }

        if (mPaymentId) {
          await PaymentService.updatePaymentStatus(mPaymentId, 'completed', pfPaymentId || undefined);
        }

        setStatus('success');
        setMessage('Your registration payment was successful.');
      } catch (error) {
        console.error('Payment success handling error:', error);
        setStatus('error');
        setMessage('Payment received, but we could not confirm your registration. Please contact support with your PayFast reference.');
      }
    };

    confirmPayment();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <FontAwesomeIcon icon={faSpinner} className="text-4xl text-primary animate-spin mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Processing Payment</h1>
            <p className="text-gray-600">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <FontAwesomeIcon icon={faCheckCircle} className="text-4xl text-green-500 mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Registration Complete!</h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="space-y-3">
              <Link
                to="/events"
                className="block w-full bg-primary text-white py-3 px-6 rounded-lg hover:bg-orange-600 transition-colors"
              >
                View All Events
              </Link>
              <Link
                to="/dashboard"
                className="block w-full border border-gray-300 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Back to Dashboard
              </Link>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-red-500 mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Payment Issue</h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <Link
              to="/events"
              className="block w-full bg-primary text-white py-3 px-6 rounded-lg hover:bg-orange-600 transition-colors"
            >
              Back to Events
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
